import * as i0 from '@angular/core';
import { ViewChild, Input, Optional, Component, EventEmitter, Output, NgModule } from '@angular/core';
import * as i3 from '@angular/forms';
import * as i4 from '@yourcause/common/core-forms';
import { FormControlComponent, CoreFormsModule } from '@yourcause/common/core-forms';
import * as i1 from '@yourcause/common/i18n';
import { I18nModule } from '@yourcause/common/i18n';
import * as i2 from '@yourcause/common/currency';
import { CurrencyModule } from '@yourcause/common/currency';
import * as i7 from 'angular-imask';
import { IMaskDirective, IMaskModule } from 'angular-imask';
import { isEqual } from 'lodash';
import * as i5 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i6 from '@yourcause/common/icons';
import { IconsModule } from '@yourcause/common/icons';
import * as i8 from 'ngx-bootstrap/popover';
import { PopoverModule } from 'ngx-bootstrap/popover';
import * as i9 from '@yourcause/common/buttons';
import { ButtonModule } from '@yourcause/common/buttons';
import * as i10 from '@yourcause/common/word-counter';
import { WordCounterModule } from '@yourcause/common/word-counter';
import * as i2$1 from '@yourcause/common/typeahead-select';
import { TypeaheadSelectModule } from '@yourcause/common/typeahead-select';

class InputComponent extends FormControlComponent {
    constructor(i18n, moneyService, formGroupDirective, formControlService) {
        super(formGroupDirective, formControlService);
        this.i18n = i18n;
        this.moneyService = moneyService;
        this.formGroupDirective = formGroupDirective;
        this.isNumber = false;
        this.inputClass = '';
        this.noBorderRadius = false;
        this.autofocus = false;
        this.isLocked = false;
        this.placeholder = '';
        this.popoverText = '';
        this.danger = false; // red text
        this.prefixSuffixFlexBasis = '50%';
        this.showWordCount = false;
        this.showCharCount = false;
        this.disablePaste = false;
        /* For when I18nNumberMask is true, track the masked value */
        this._maskValue = '';
        this.isAfterInit = false;
    }
    set value(value) {
        const changed = !isEqual(this._value, value);
        if (changed) {
            this._value = value;
            if (this.hasMask) {
                this.getAndSetMaskValue();
                /* Wait for _value to be passed to template */
                setTimeout(() => {
                    this.handleMaskChange(true);
                });
            }
        }
    }
    get value() {
        return this._value;
    }
    get hasMask() {
        return this.isNumber || !!this.customMaskPattern;
    }
    get inputValue() {
        return this.disabled && this.disabledDisplayValue ?
            this.disabledDisplayValue :
            (this.hasMask ? this._maskValue : this._value);
    }
    ngOnInit() {
        super.ngOnInit();
        if (this.isNumber || this.customMaskPattern) {
            this.initMask();
        }
        this.isAfterInit = true;
    }
    ngOnChanges(changes) {
        if (this.isAfterInit) {
            if (changes.customMaskPattern) {
                if (!!this.customMaskPattern) {
                    const isNewMask = !this.maskConfig;
                    if (isNewMask) {
                        this.initMask();
                    }
                    else {
                        this.setMaskConfig();
                        this.iMask.maskRef.updateOptions(this.maskConfig);
                    }
                }
                else { // Mask was removed from input
                    this.maskConfig = undefined;
                    this.iMask?.maskRef?.destroy();
                    this.clearValue();
                }
            }
            if (changes.label || changes.placeholder || changes.value) {
                if (!this.label && !!this.placeholder) {
                    this.label = this.placeholder;
                    this.srOnlyLabel = true;
                }
            }
        }
    }
    ngAfterViewInit() {
        if (this.autofocus) {
            this.focusOnInput();
        }
        this.thisInput.nativeElement.onwheel = (_e) => {
            _e.preventDefault();
        };
        this.thisInput.nativeElement.onpaste = (_e) => {
            if (this.disablePaste) {
                _e.preventDefault();
                return false;
            }
            setTimeout(() => {
                this._onChangeEvent(_e);
            });
            return true;
        };
    }
    initMask() {
        this.setMaskConfig();
        this.getAndSetMaskValue();
    }
    getFormattingData() {
        return this.moneyService.getFormattingData(this.numberOfDecimals, this.requireDecimal, this.currencyData, this.i18n.numberFormattingData);
    }
    setMaskConfig() {
        if (this.isNumber) {
            const formattingData = this.getFormattingData();
            const min = !!this.min || this.min === 0 ?
                this.min :
                -99999999999999;
            this.maskConfig = {
                mask: Number, // enable number mask
                thousandsSeparator: formattingData.thousands,
                scale: formattingData.decimalLimit, // digits after point, 0 for integers
                padFractionalZeros: !!this.currencyData, // if true, then pads zeros at end to the length of scale
                normalizeZeros: true, // appends or removes zeros at ends
                radix: formattingData.decimal, // fractional delimiter
                mapToRadix: [formattingData.decimal], // symbols to process as radix
                min,
                max: this.max
            };
        }
        else if (!!this.customMaskPattern) {
            this.maskConfig = {
                mask: this.customMaskPattern,
                lazy: !!this.placeholder ? true : false // make placeholder always visible
            };
        }
    }
    getAndSetMaskValue() {
        if (this.hasMask) {
            let maskVal = this._value;
            if (this.isNumber) {
                if (this._value || this._value === 0) {
                    maskVal = this.i18n.formatNumber(this._value, this.getFormattingData());
                }
                else {
                    maskVal = '';
                }
            }
            this.setMaskValue(maskVal);
            if (!!this.iMask && this.iMask.maskValue !== maskVal) {
                this.iMask.maskValue = maskVal;
            }
        }
    }
    setMaskValue(maskValue) {
        if (maskValue !== this._maskValue) {
            this._maskValue = maskValue;
            if (this.thisInput?.nativeElement) {
                this.thisInput.nativeElement.value = this.inputValue;
            }
        }
    }
    _onIconClick(icon) {
        this.focusOnInput();
        if (icon.onIconClick) {
            icon.onIconClick();
        }
    }
    _onClearIconClick() {
        this.clearValue();
        this.focusOnInput();
    }
    focusOnInput() {
        if (this.thisInput.nativeElement?.focus) {
            setTimeout(() => this.thisInput.nativeElement.focus(), 100);
        }
    }
    clearValue() {
        let value = '';
        if (this.isNumber) {
            value = null;
        }
        this._onChange(value);
        if (this.thisInput.nativeElement) {
            this.thisInput.nativeElement.value = this.inputValue;
        }
    }
    /**
     *
     * @param doStaticChanges if true, pad decimals, otherwise emit the change
     */
    handleMaskChange(doStaticChanges) {
        let valueToSave = this.iMask?.maskValue;
        if (this.isNumber) {
            valueToSave = this.iMask?.maskRef?.unmaskedValue;
            if (!!valueToSave) {
                valueToSave = +valueToSave;
            }
            else {
                valueToSave = null;
            }
        }
        else {
            if (!!this.iMask?.maskRef) {
                if (!this.iMask.maskRef.unmaskedValue) {
                    // this means the value was cleared and should be empty
                    valueToSave = '';
                }
            }
        }
        if (doStaticChanges) {
            this.setMaskValue(this.iMask?.maskValue);
        }
        else {
            this._onChange(valueToSave);
        }
    }
    _onChangeEvent(e) {
        if (this.hasMask) {
            this.handleMaskChange(false);
        }
        else {
            const target = e.target;
            const value = target.value;
            if (!!value && !!this.customRegexPattern) {
                target.value = value.replace(this.customRegexPattern, '');
            }
            this._onChange(target.value);
        }
    }
    _doOnChange(value, fromControlChange = false) {
        super._doOnChange(value, fromControlChange);
        if (fromControlChange) {
            this.getAndSetMaskValue();
        }
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: InputComponent, deps: [{ token: i1.I18nService }, { token: i2.MoneyService }, { token: i3.FormGroupDirective, optional: true }, { token: i4.FormControlService }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.2.10", type: InputComponent, isStandalone: false, selector: "yc-input", inputs: { isNumber: "isNumber", currencyData: "currencyData", numberOfDecimals: "numberOfDecimals", requireDecimal: "requireDecimal", inputClass: "inputClass", rounded: "rounded", noBorderRadius: "noBorderRadius", autofocus: "autofocus", isLocked: "isLocked", placeholder: "placeholder", popoverText: "popoverText", danger: "danger", maxLength: "maxLength", min: "min", max: "max", prefix: "prefix", suffix: "suffix", prefixSuffixFlexBasis: "prefixSuffixFlexBasis", showWordCount: "showWordCount", showCharCount: "showCharCount", disablePaste: "disablePaste", customMaskPattern: "customMaskPattern", customRegexPattern: "customRegexPattern" }, viewQueries: [{ propertyName: "thisInput", first: true, predicate: ["thisInput"], descendants: true }, { propertyName: "iMask", first: true, predicate: IMaskDirective, descendants: true }], usesInheritance: true, usesOnChanges: true, ngImport: i0, template: "<div class=\"form-group\"\n  [class.mb-0]=\"noMarginBottom\"\n  [class.ng-invalid]=\"!!(error || customError) && isDirty\">\n  <yc-form-label\n    [srOnly]=\"srOnlyLabel\"\n    [name]=\"name\"\n    [label]=\"label\"\n    [description]=\"description\"\n    [tooltipText]=\"tooltipText\"\n    [popoverTemplate]=\"popoverTemplate\"\n    [showQuestionMarkForTooltip]=\"showQuestionMarkForTooltip\"\n    [asteriskForRequired]=\"addRequiredAsterisk\"\n    [customIcon]=\"customLabelIcon\"\n    [customIconTooltip]=\"customLabelIconTooltip\"\n    [customIconClass]=\"customLabelIconClass\">\n  </yc-form-label>\n  <div class=\"form-input-wrapper {{ inputClass }}\"\n    [class.with-clear]=\"showClear\"\n    [class.has-value]=\"!!_value\"\n    [class.d-flex]=\"suffix || prefix\"\n    [class.yc-input-icon-left]=\"leftIcon\"\n    [class.yc-input-icon-right]=\"rightIcons?.length\">\n    <!-- Icon Left -->\n    <ng-container *ngIf=\"leftIcon && !leftIcon.isStaticText\">\n      <yc-fa class=\"yc-left-input-icon\"\n        [icon]=\"leftIcon.icon\"\n        [ngClass]=\"leftIcon.iconClass\"\n        [class.pointer]=\"leftIcon.onIconClick\"\n        [tooltipText]=\"leftIcon.tooltip\"\n        (onClick)=\"_onIconClick(leftIcon)\">\n      </yc-fa>\n    </ng-container>\n    <!-- Static Text Icon Left -->\n    <ng-container *ngIf=\"leftIcon && leftIcon.isStaticText\">\n      <div class=\"yc-left-input-icon static-text\"\n        [ngClass]=\"leftIcon.iconClass\">\n        {{ leftIcon.icon }}\n      </div>\n    </ng-container>\n    <ng-container *ngIf=\"prefix\">\n      <div class=\"prefix-suffix-block prefix\"\n        [ngStyle]=\"{\n          'flex-basis': prefixSuffixFlexBasis\n        }\">\n        {{ prefix }}\n      </div>\n    </ng-container>\n    <input\n      #thisInput\n      [attr.maxlength]=\"maxLength\"\n      [attr.aria-required]=\"addRequiredAsterisk\"\n      [placeholder]=\"placeholder\"\n      [disabled]=\"disabled || control.disabled\"\n      [type]=\"type\"\n      [min]=\"min\"\n      [max]=\"max\"\n      [imask]=\"maskConfig\"\n      [id]=\"name\"\n      [tabindex]=\"tabIndex\"\n      autocomplete=\"on\"\n      [class.no-border-radius]=\"noBorderRadius\"\n      [class.has-suffix]=\"suffix\"\n      [class.has-prefix]=\"prefix\"\n      [class.has-prefix-and-suffix]=\"prefix && suffix\"\n      class=\"yc-component form-control\"\n      [class.form-control-rounded]=\"rounded\"\n      [class.text-danger]=\"danger\"\n      [value]=\"inputValue\"\n      [popover]=\"popoverText\"\n      triggers=\"focus\"\n      (blur)=\"onBlur.emit()\"\n      (keyup)=\"_onChangeEvent($event)\"\n      (change)=\"_onChangeEvent($event)\">\n\n    <ng-container *ngIf=\"suffix\">\n      <div class=\"prefix-suffix-block suffix\"\n        [ngStyle]=\"{\n          'flex-basis': prefixSuffixFlexBasis\n        }\">\n        <div>\n          {{ suffix }}\n        </div>\n      </div>\n    </ng-container>\n      \n    <!-- Icons Right -->\n    <ng-container *ngFor=\"let item of rightIcons; let last = last;\">\n      <ng-container *ngIf=\"item && !item.isStaticText\">\n        <yc-fa class=\"yc-right-input-icon\"\n          [class.me-4]=\"!last && rightIcons.length > 1\"\n          [ngClass]=\"item.iconClass\"\n          [icon]=\"item.icon\"\n          [class.pointer]=\"item.onIconClick\"\n          [tooltipText]=\"item.tooltip\"\n          (onClick)=\"_onIconClick(item)\">\n        </yc-fa>\n      </ng-container>\n      <!-- Static Text Icon Right -->\n      <ng-container *ngIf=\"item && item.isStaticText\">\n        <div class=\"yc-right-input-icon static-text\"\n          [ngClass]=\"item.iconClass\">\n          {{ item.icon }}\n        </div>\n      </ng-container>\n    </ng-container>\n    \n\n    <yc-button\n      class=\"yc-right-clear-icon\"\n      [textDanger]=\"true\"\n      [tooltipText]=\"'common:linkClear' | ycI18n: 'Clear'\"\n      [icon]=\"true\"\n      [fontAwesomeIcon]=\"'times'\"\n      (onClick)=\"_onClearIconClick()\">\n    </yc-button>\n    <yc-fa *ngIf=\"control.status === 'PENDING'\"\n      class=\"yc-input-status-icon\"\n      icon=\"refresh fa-spin\">\n    </yc-fa>\n    <yc-fa class=\"yc-input-status-icon\"\n      icon=\"exclamation-circle\">\n    </yc-fa>\n    <yc-fa *ngIf=\"isLocked\" class=\"yc-input-status-icon\"\n      icon=\"lock\">\n    </yc-fa>\n\n  </div>\n  <!-- Word Counter -->\n  <yc-word-counter *ngIf=\"showWordCount || showCharCount\"\n    class=\"d-flex flex-grow-1 justify-content-end\"\n    [showWordCount]=\"showWordCount\"\n    [showCharCount]=\"showCharCount\"\n    [inputValue]=\"_value\">\n  </yc-word-counter>\n  <ng-content></ng-content>\n\n  <yc-form-help-or-error\n    [error]=\"error\"\n    [customError]=\"customError\"\n    [showError]=\"showError\"\n    [showHelp]=\"showHelp\"\n    [help]=\"help\"\n    [helpTextClass]=\"helpTextClass\">\n  </yc-form-help-or-error>\n</div>\n", styles: ["input::placeholder{font-weight:initial;opacity:1;color:#4a4d50}.no-border-radius{border-radius:0}.form-control.has-suffix{border-radius:.25rem 0 0 .25rem!important}.form-control.has-prefix{border-radius:0 .25rem .25rem 0!important}.form-control.has-prefix-and-suffix{border-radius:0!important}.prefix-suffix-block{height:36px;background-color:#efefef;display:flex;align-items:center;border:1px solid gainsboro;padding:1rem;flex-basis:50%}.prefix{border-radius:.25rem 0 0 .25rem}.suffix{border-radius:0 .25rem .25rem 0}\n"], dependencies: [{ kind: "directive", type: i5.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i5.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i5.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i5.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "component", type: i6.FontAwesomeComponent, selector: "yc-fa", inputs: ["color", "icon", "tooltipText", "popoverTemplate", "light", "regular", "solid"], outputs: ["onClick"] }, { kind: "component", type: i4.FormLabelComponent, selector: "yc-form-label", inputs: ["name", "label", "srOnly", "description", "tabIndex", "cursorStyle", "tooltipText", "popoverTemplate", "showQuestionMarkForTooltip", "asteriskForRequired", "forRadioButtons", "customIcon", "customIconTooltip", "customIconClass", "forFileUpload", "triggerUpload"] }, { kind: "component", type: i4.FormHelpOrErrorComponent, selector: "yc-form-help-or-error", inputs: ["error", "customError", "showError", "showHelp", "help", "helpTextClass"] }, { kind: "directive", type: i7.IMaskDirective, selector: "[imask]", inputs: ["imask", "unmask", "imaskElement"], outputs: ["accept", "complete"], exportAs: ["imask"] }, { kind: "directive", type: i8.PopoverDirective, selector: "[popover]", inputs: ["adaptivePosition", "boundariesElement", "popover", "popoverContext", "popoverTitle", "placement", "outsideClick", "triggers", "container", "containerClass", "isOpen", "delay"], outputs: ["onShown", "onHidden"], exportAs: ["bs-popover"] }, { kind: "component", type: i9.ButtonComponent, selector: "yc-button", inputs: ["tabIndex", "disabled", "size", "fontSize", "buttonType", "primary", "link", "linkPath", "icon", "secondary", "textDanger", "textWarning", "textPrimary", "textSuccess", "borderOnHover", "noClick", "badgeValue", "value", "ariaDescribedBy", "minWidth", "alignTop", "tooltipText", "popoverTemplate", "container", "popoverContext", "popoverIsOpen", "popoverOutsideClickToClose", "placement", "adaptivePosition", "langKey", "defaultText", "translatedText", "fontAwesomeIcon", "isDropdownButton", "isFooterButton", "dropdownDirection", "dropdownActions", "isDropdownToggle", "isSplitDropdownToggle", "preventClickTracking", "leftAlignLinkText", "analyticsEventName", "customColor", "buttonElementId", "includeExpandedAria", "ariaExpanded"], outputs: ["onClick", "popoverIsOpenChange", "onPopoverReady", "onPopoverClick"] }, { kind: "component", type: i10.WordCounterComponent, selector: "yc-word-counter", inputs: ["showWordCount", "showCharCount", "showMaxLength", "maxLength", "inputValue"] }, { kind: "pipe", type: i1.I18nPipe, name: "ycI18n" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: InputComponent, decorators: [{
            type: Component,
            args: [{ selector: 'yc-input', standalone: false, template: "<div class=\"form-group\"\n  [class.mb-0]=\"noMarginBottom\"\n  [class.ng-invalid]=\"!!(error || customError) && isDirty\">\n  <yc-form-label\n    [srOnly]=\"srOnlyLabel\"\n    [name]=\"name\"\n    [label]=\"label\"\n    [description]=\"description\"\n    [tooltipText]=\"tooltipText\"\n    [popoverTemplate]=\"popoverTemplate\"\n    [showQuestionMarkForTooltip]=\"showQuestionMarkForTooltip\"\n    [asteriskForRequired]=\"addRequiredAsterisk\"\n    [customIcon]=\"customLabelIcon\"\n    [customIconTooltip]=\"customLabelIconTooltip\"\n    [customIconClass]=\"customLabelIconClass\">\n  </yc-form-label>\n  <div class=\"form-input-wrapper {{ inputClass }}\"\n    [class.with-clear]=\"showClear\"\n    [class.has-value]=\"!!_value\"\n    [class.d-flex]=\"suffix || prefix\"\n    [class.yc-input-icon-left]=\"leftIcon\"\n    [class.yc-input-icon-right]=\"rightIcons?.length\">\n    <!-- Icon Left -->\n    <ng-container *ngIf=\"leftIcon && !leftIcon.isStaticText\">\n      <yc-fa class=\"yc-left-input-icon\"\n        [icon]=\"leftIcon.icon\"\n        [ngClass]=\"leftIcon.iconClass\"\n        [class.pointer]=\"leftIcon.onIconClick\"\n        [tooltipText]=\"leftIcon.tooltip\"\n        (onClick)=\"_onIconClick(leftIcon)\">\n      </yc-fa>\n    </ng-container>\n    <!-- Static Text Icon Left -->\n    <ng-container *ngIf=\"leftIcon && leftIcon.isStaticText\">\n      <div class=\"yc-left-input-icon static-text\"\n        [ngClass]=\"leftIcon.iconClass\">\n        {{ leftIcon.icon }}\n      </div>\n    </ng-container>\n    <ng-container *ngIf=\"prefix\">\n      <div class=\"prefix-suffix-block prefix\"\n        [ngStyle]=\"{\n          'flex-basis': prefixSuffixFlexBasis\n        }\">\n        {{ prefix }}\n      </div>\n    </ng-container>\n    <input\n      #thisInput\n      [attr.maxlength]=\"maxLength\"\n      [attr.aria-required]=\"addRequiredAsterisk\"\n      [placeholder]=\"placeholder\"\n      [disabled]=\"disabled || control.disabled\"\n      [type]=\"type\"\n      [min]=\"min\"\n      [max]=\"max\"\n      [imask]=\"maskConfig\"\n      [id]=\"name\"\n      [tabindex]=\"tabIndex\"\n      autocomplete=\"on\"\n      [class.no-border-radius]=\"noBorderRadius\"\n      [class.has-suffix]=\"suffix\"\n      [class.has-prefix]=\"prefix\"\n      [class.has-prefix-and-suffix]=\"prefix && suffix\"\n      class=\"yc-component form-control\"\n      [class.form-control-rounded]=\"rounded\"\n      [class.text-danger]=\"danger\"\n      [value]=\"inputValue\"\n      [popover]=\"popoverText\"\n      triggers=\"focus\"\n      (blur)=\"onBlur.emit()\"\n      (keyup)=\"_onChangeEvent($event)\"\n      (change)=\"_onChangeEvent($event)\">\n\n    <ng-container *ngIf=\"suffix\">\n      <div class=\"prefix-suffix-block suffix\"\n        [ngStyle]=\"{\n          'flex-basis': prefixSuffixFlexBasis\n        }\">\n        <div>\n          {{ suffix }}\n        </div>\n      </div>\n    </ng-container>\n      \n    <!-- Icons Right -->\n    <ng-container *ngFor=\"let item of rightIcons; let last = last;\">\n      <ng-container *ngIf=\"item && !item.isStaticText\">\n        <yc-fa class=\"yc-right-input-icon\"\n          [class.me-4]=\"!last && rightIcons.length > 1\"\n          [ngClass]=\"item.iconClass\"\n          [icon]=\"item.icon\"\n          [class.pointer]=\"item.onIconClick\"\n          [tooltipText]=\"item.tooltip\"\n          (onClick)=\"_onIconClick(item)\">\n        </yc-fa>\n      </ng-container>\n      <!-- Static Text Icon Right -->\n      <ng-container *ngIf=\"item && item.isStaticText\">\n        <div class=\"yc-right-input-icon static-text\"\n          [ngClass]=\"item.iconClass\">\n          {{ item.icon }}\n        </div>\n      </ng-container>\n    </ng-container>\n    \n\n    <yc-button\n      class=\"yc-right-clear-icon\"\n      [textDanger]=\"true\"\n      [tooltipText]=\"'common:linkClear' | ycI18n: 'Clear'\"\n      [icon]=\"true\"\n      [fontAwesomeIcon]=\"'times'\"\n      (onClick)=\"_onClearIconClick()\">\n    </yc-button>\n    <yc-fa *ngIf=\"control.status === 'PENDING'\"\n      class=\"yc-input-status-icon\"\n      icon=\"refresh fa-spin\">\n    </yc-fa>\n    <yc-fa class=\"yc-input-status-icon\"\n      icon=\"exclamation-circle\">\n    </yc-fa>\n    <yc-fa *ngIf=\"isLocked\" class=\"yc-input-status-icon\"\n      icon=\"lock\">\n    </yc-fa>\n\n  </div>\n  <!-- Word Counter -->\n  <yc-word-counter *ngIf=\"showWordCount || showCharCount\"\n    class=\"d-flex flex-grow-1 justify-content-end\"\n    [showWordCount]=\"showWordCount\"\n    [showCharCount]=\"showCharCount\"\n    [inputValue]=\"_value\">\n  </yc-word-counter>\n  <ng-content></ng-content>\n\n  <yc-form-help-or-error\n    [error]=\"error\"\n    [customError]=\"customError\"\n    [showError]=\"showError\"\n    [showHelp]=\"showHelp\"\n    [help]=\"help\"\n    [helpTextClass]=\"helpTextClass\">\n  </yc-form-help-or-error>\n</div>\n", styles: ["input::placeholder{font-weight:initial;opacity:1;color:#4a4d50}.no-border-radius{border-radius:0}.form-control.has-suffix{border-radius:.25rem 0 0 .25rem!important}.form-control.has-prefix{border-radius:0 .25rem .25rem 0!important}.form-control.has-prefix-and-suffix{border-radius:0!important}.prefix-suffix-block{height:36px;background-color:#efefef;display:flex;align-items:center;border:1px solid gainsboro;padding:1rem;flex-basis:50%}.prefix{border-radius:.25rem 0 0 .25rem}.suffix{border-radius:0 .25rem .25rem 0}\n"] }]
        }], ctorParameters: () => [{ type: i1.I18nService }, { type: i2.MoneyService }, { type: i3.FormGroupDirective, decorators: [{
                    type: Optional
                }] }, { type: i4.FormControlService }], propDecorators: { isNumber: [{
                type: Input
            }], currencyData: [{
                type: Input
            }], numberOfDecimals: [{
                type: Input
            }], requireDecimal: [{
                type: Input
            }], inputClass: [{
                type: Input
            }], rounded: [{
                type: Input
            }], noBorderRadius: [{
                type: Input
            }], autofocus: [{
                type: Input
            }], isLocked: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], popoverText: [{
                type: Input
            }], danger: [{
                type: Input
            }], maxLength: [{
                type: Input
            }], min: [{
                type: Input
            }], max: [{
                type: Input
            }], prefix: [{
                type: Input
            }], suffix: [{
                type: Input
            }], prefixSuffixFlexBasis: [{
                type: Input
            }], showWordCount: [{
                type: Input
            }], showCharCount: [{
                type: Input
            }], disablePaste: [{
                type: Input
            }], customMaskPattern: [{
                type: Input
            }], customRegexPattern: [{
                type: Input
            }], thisInput: [{
                type: ViewChild,
                args: ['thisInput']
            }], iMask: [{
                type: ViewChild,
                args: [IMaskDirective]
            }] } });

class CurrencyComponent {
    constructor() {
        this.tabIndex = 0;
        this.currencyOptions = [];
        this.amountChange = new EventEmitter();
        this.currencyChange = new EventEmitter();
        this.hasErrors = false;
        this.toggleCurrency = () => {
            this.hasToggled = !this.hasToggled;
            this.setIcons();
        };
    }
    get amountToShow() {
        if (this.canToggle) {
            return this.hasToggled ? this.equivalent : this.amount;
        }
        return this.amount;
    }
    get currencyToShow() {
        if (this.canToggle) {
            return this.hasToggled ? this.equivalentCurrency : this.currency;
        }
        return this.currency;
    }
    ngOnInit() {
        this.setIcons();
    }
    ngOnChanges(changes) {
        if (changes.currency || changes.canToggle) {
            this.setIcons();
        }
        if (changes.isDirtyOverride || changes.errors) {
            if (this.errors) {
                const numberOfErrors = Object.keys(this.errors).length;
                this.hasErrors = numberOfErrors > 0 && this.isDirtyOverride;
            }
            else {
                this.hasErrors = false;
            }
        }
    }
    setIcons() {
        this.leftIcon = this.currency ? {
            icon: this.formattingData[this.currencyToShow]?.symbol,
            isStaticText: true
        } : null;
        this.rightIcons = this.canToggle ?
            [{
                    icon: 'globe',
                    onIconClick: this.toggleCurrency
                }] :
            [];
    }
    handleAmountChange(event) {
        this.amount = event;
        this.amountChange.emit(event);
    }
    handleCurrencyChange(event) {
        if (this.currency !== event) {
            this.currencyChange.emit(event);
        }
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: CurrencyComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.2.10", type: CurrencyComponent, isStandalone: false, selector: "yc-currency", inputs: { name: "name", amount: "amount", equivalent: "equivalent", currency: "currency", equivalentCurrency: "equivalentCurrency", canToggle: "canToggle", tabIndex: "tabIndex", label: "label", disabled: "disabled", hideLabel: "hideLabel", description: "description", currencyOptions: "currencyOptions", currencyLocked: "currencyLocked", currencyData: "currencyData", isRequired: "isRequired", formattingData: "formattingData", errors: "errors", isDirtyOverride: "isDirtyOverride", customLabelIcon: "customLabelIcon", customLabelIconTooltip: "customLabelIconTooltip", customLabelIconClass: "customLabelIconClass" }, outputs: { amountChange: "amountChange", currencyChange: "currencyChange" }, usesOnChanges: true, ngImport: i0, template: "\n<div class=\"currency-wrapper align-items-end\"\n  [class.align-items-center]=\"hasErrors\">\n  <div class=\"me-3 flex-grow-1\">\n    <yc-input\n      [name]=\"name\"\n      [value]=\"amountToShow\"\n      [disabled]=\"disabled\"\n      [isDirtyOverride]=\"isDirtyOverride\"\n      [label]=\"label\"\n      [description]=\"description\"\n      [tabIndex]=\"tabIndex\"\n      [errors]=\"errors\"\n      [srOnlyLabel]=\"hideLabel\"\n      [addRequiredAsterisk]=\"isRequired\"\n      [leftIcon]=\"leftIcon\"\n      [isNumber]=\"true\"\n      [rightIcons]=\"rightIcons\"\n      [customLabelIcon]=\"customLabelIcon\"\n      [customLabelIconTooltip]=\"customLabelIconTooltip\"\n      [customLabelIconClass]=\"customLabelIconClass\"\n      [currencyData]=\"formattingData[currencyToShow]\"\n      (valueChange)=\"handleAmountChange($event)\">\n    </yc-input>\n  </div>\n  <div>\n    <ng-container *ngIf=\"!currencyLocked\">\n      <yc-typeahead-select\n        [value]=\"currencyToShow\"\n        [disabled]=\"disabled\"\n        [hideBorder]=\"!disabled\"\n        [items]=\"currencyOptions\"\n        [showClear]=\"false\"\n        (valueChange)=\"handleCurrencyChange($event)\">\n      </yc-typeahead-select>\n    </ng-container>\n    <div *ngIf=\"currencyLocked\"\n      class=\"add-margin-bottom\">\n      {{ currencyToShow }}\n    </div>\n  </div>\n</div>\n\n\n", styles: [".add-margin-bottom{margin-bottom:.75rem}.currency-wrapper{display:flex}\n"], dependencies: [{ kind: "directive", type: i5.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: i2$1.TypeaheadSelectComponent, selector: "yc-typeahead-select", inputs: ["items", "dropdownPosition", "notFoundText", "disabled", "multi", "formatAsArray", "hideBorder", "placeholder", "isLocked", "showHelp", "showError", "showClear", "noBorderRadius", "useEmptyArray", "useVirtualScroll", "loading", "useLabelTemplate", "showSelectAllThatApplyHelpText"], outputs: ["onItemAdded", "onItemRemoved"] }, { kind: "component", type: InputComponent, selector: "yc-input", inputs: ["isNumber", "currencyData", "numberOfDecimals", "requireDecimal", "inputClass", "rounded", "noBorderRadius", "autofocus", "isLocked", "placeholder", "popoverText", "danger", "maxLength", "min", "max", "prefix", "suffix", "prefixSuffixFlexBasis", "showWordCount", "showCharCount", "disablePaste", "customMaskPattern", "customRegexPattern"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: CurrencyComponent, decorators: [{
            type: Component,
            args: [{ selector: 'yc-currency', standalone: false, template: "\n<div class=\"currency-wrapper align-items-end\"\n  [class.align-items-center]=\"hasErrors\">\n  <div class=\"me-3 flex-grow-1\">\n    <yc-input\n      [name]=\"name\"\n      [value]=\"amountToShow\"\n      [disabled]=\"disabled\"\n      [isDirtyOverride]=\"isDirtyOverride\"\n      [label]=\"label\"\n      [description]=\"description\"\n      [tabIndex]=\"tabIndex\"\n      [errors]=\"errors\"\n      [srOnlyLabel]=\"hideLabel\"\n      [addRequiredAsterisk]=\"isRequired\"\n      [leftIcon]=\"leftIcon\"\n      [isNumber]=\"true\"\n      [rightIcons]=\"rightIcons\"\n      [customLabelIcon]=\"customLabelIcon\"\n      [customLabelIconTooltip]=\"customLabelIconTooltip\"\n      [customLabelIconClass]=\"customLabelIconClass\"\n      [currencyData]=\"formattingData[currencyToShow]\"\n      (valueChange)=\"handleAmountChange($event)\">\n    </yc-input>\n  </div>\n  <div>\n    <ng-container *ngIf=\"!currencyLocked\">\n      <yc-typeahead-select\n        [value]=\"currencyToShow\"\n        [disabled]=\"disabled\"\n        [hideBorder]=\"!disabled\"\n        [items]=\"currencyOptions\"\n        [showClear]=\"false\"\n        (valueChange)=\"handleCurrencyChange($event)\">\n      </yc-typeahead-select>\n    </ng-container>\n    <div *ngIf=\"currencyLocked\"\n      class=\"add-margin-bottom\">\n      {{ currencyToShow }}\n    </div>\n  </div>\n</div>\n\n\n", styles: [".add-margin-bottom{margin-bottom:.75rem}.currency-wrapper{display:flex}\n"] }]
        }], propDecorators: { name: [{
                type: Input
            }], amount: [{
                type: Input
            }], equivalent: [{
                type: Input
            }], currency: [{
                type: Input
            }], equivalentCurrency: [{
                type: Input
            }], canToggle: [{
                type: Input
            }], tabIndex: [{
                type: Input
            }], label: [{
                type: Input
            }], disabled: [{
                type: Input
            }], hideLabel: [{
                type: Input
            }], description: [{
                type: Input
            }], currencyOptions: [{
                type: Input
            }], currencyLocked: [{
                type: Input
            }], currencyData: [{
                type: Input
            }], isRequired: [{
                type: Input
            }], formattingData: [{
                type: Input
            }], errors: [{
                type: Input
            }], isDirtyOverride: [{
                type: Input
            }], customLabelIcon: [{
                type: Input
            }], customLabelIconTooltip: [{
                type: Input
            }], customLabelIconClass: [{
                type: Input
            }], amountChange: [{
                type: Output
            }], currencyChange: [{
                type: Output
            }] } });

class InputModule {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: InputModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "19.2.10", ngImport: i0, type: InputModule, declarations: [InputComponent,
            CurrencyComponent], imports: [CommonModule,
            IconsModule,
            CoreFormsModule,
            IMaskModule,
            PopoverModule,
            ButtonModule,
            I18nModule,
            WordCounterModule,
            CurrencyModule,
            TypeaheadSelectModule], exports: [InputComponent,
            CurrencyComponent] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: InputModule, imports: [CommonModule,
            IconsModule,
            CoreFormsModule,
            IMaskModule,
            PopoverModule,
            ButtonModule,
            I18nModule,
            WordCounterModule,
            CurrencyModule,
            TypeaheadSelectModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: InputModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        IconsModule,
                        CoreFormsModule,
                        IMaskModule,
                        PopoverModule,
                        ButtonModule,
                        I18nModule,
                        WordCounterModule,
                        CurrencyModule,
                        TypeaheadSelectModule
                    ],
                    declarations: [
                        InputComponent,
                        CurrencyComponent
                    ],
                    exports: [
                        InputComponent,
                        CurrencyComponent
                    ]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { CurrencyComponent, InputComponent, InputModule };
